/**
 * Opening Book module - Database of common chess openings
 * 
 * This module provides a collection of standard chess openings that the AI
 * can play instead of calculating from scratch. This improves both the quality
 * of opening play (following known theory) and performance (instant move selection).
 * 
 * The opening book uses algebraic notation (e4, Nf3, etc.) and weighted random
 * selection to provide variety while preferring stronger moves.
 */

import { GameState, Move } from './types';
import { generateLegalMoves, makeMove } from './moves';
import { toFEN } from './board';
import { moveToAlgebraic } from './notation';

/**
 * Represents a possible move from the opening book
 * Weight determines relative probability (higher = more likely to be played)
 */
export interface BookMove {
  notation: string;  // Algebraic notation (e.g., "e4", "Nf3", "O-O")
  weight: number;    // Relative probability weight (1-100)
}

/**
 * Represents a position in the opening book
 * Positions are keyed by FEN (without move counters for simplicity)
 */
export interface BookPosition {
  fen: string;
  moves: BookMove[];
  name?: string;  // Optional opening name for display (e.g., "Ruy Lopez")
}

/**
 * Opening book database
 * Contains common opening positions and their best continuations
 * 
 * Weights guide move selection:
 * - 100: Main theoretical line
 * - 50-80: Strong alternative lines
 * - 20-40: Playable but less common lines
 * - 1-10: Rare/experimental lines
 */
const openingBookData: BookPosition[] = [
  // Starting position - Main openings
  {
    name: "Starting Position",
    fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "e4", weight: 100 },   // King's Pawn Opening (most popular)
      { notation: "d4", weight: 80 },    // Queen's Pawn Opening
      { notation: "Nf3", weight: 50 },   // Reti Opening
      { notation: "c4", weight: 40 },    // English Opening
      { notation: "g3", weight: 20 },    // King's Fianchetto
      { notation: "b3", weight: 10 },    // Larsen's Opening
    ]
  },
  
  // After 1.e4 - Black's main defenses
  {
    name: "King's Pawn Opening",
    fen: "rnbqkbnr/pppppppp/8/8/4P3/8/PPPP1PPP/RNBQKBNR b KQkq e3",
    moves: [
      { notation: "e5", weight: 100 },   // Open Game (symmetrical)
      { notation: "c5", weight: 90 },    // Sicilian Defense (very popular)
      { notation: "e6", weight: 60 },    // French Defense
      { notation: "c6", weight: 50 },    // Caro-Kann Defense
      { notation: "d6", weight: 40 },    // Pirc Defense
      { notation: "Nf6", weight: 35 },   // Alekhine's Defense
      { notation: "g6", weight: 20 },    // Modern Defense
    ]
  },
  
  // After 1.e4 e5 - White's second move
  {
    name: "Open Game (1.e4 e5)",
    fen: "rnbqkbnr/pppp1ppp/8/4p3/4P3/8/PPPP1PPP/RNBQKBNR w KQkq e6",
    moves: [
      { notation: "Nf3", weight: 100 },  // Most common, attacks e5
      { notation: "Bc4", weight: 30 },   // Italian Game preparation
      { notation: "Nc3", weight: 25 },   // Vienna Game
      { notation: "f4", weight: 20 },    // King's Gambit
      { notation: "d4", weight: 15 },    // Center Game
    ]
  },
  
  // After 1.e4 e5 2.Nf3 - Black's main defenses
  {
    name: "Open Game: 2.Nf3",
    fen: "rnbqkbnr/pppp1ppp/8/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq -",
    moves: [
      { notation: "Nc6", weight: 100 },  // Most natural, develops piece
      { notation: "Nf6", weight: 60 },   // Petrov's Defense
      { notation: "d6", weight: 30 },    // Philidor Defense
      { notation: "f5", weight: 10 },    // Latvian Gambit
    ]
  },
  
  // After 1.e4 e5 2.Nf3 Nc6 - White's third move
  {
    name: "Open Game: 2...Nc6",
    fen: "r1bqkbnr/pppp1ppp/2n5/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq -",
    moves: [
      { notation: "Bb5", weight: 100 },  // Ruy Lopez (Spanish Opening)
      { notation: "Bc4", weight: 80 },   // Italian Game
      { notation: "d4", weight: 40 },    // Scotch Game
      { notation: "Nc3", weight: 30 },   // Four Knights Game
      { notation: "Bb5", weight: 100 },  // Ruy Lopez
    ]
  },
  
  // After 1.e4 c5 - White's response to Sicilian
  {
    name: "Sicilian Defense",
    fen: "rnbqkbnr/pp1ppppp/8/2p5/4P3/8/PPPP1PPP/RNBQKBNR w KQkq c6",
    moves: [
      { notation: "Nf3", weight: 100 },  // Main line, prepares d4
      { notation: "Nc3", weight: 60 },   // Closed Sicilian
      { notation: "d4", weight: 30 },    // Smith-Morra Gambit
      { notation: "c3", weight: 25 },    // Alapin Variation
    ]
  },
  
  // After 1.e4 c5 2.Nf3 - Black's second move in Sicilian
  {
    name: "Sicilian Defense: 2.Nf3",
    fen: "rnbqkbnr/pp1ppppp/8/2p5/4P3/5N2/PPPP1PPP/RNBQKB1R b KQkq -",
    moves: [
      { notation: "d6", weight: 80 },    // Najdorf/Dragon prep
      { notation: "Nc6", weight: 70 },   // Old Sicilian
      { notation: "e6", weight: 60 },    // Taimanov/Paulsen
      { notation: "g6", weight: 50 },    // Hyperaccelerated Dragon
    ]
  },
  
  // After 1.e4 c5 2.Nf3 d6 - White plays d4
  {
    name: "Sicilian Defense: Open Sicilian",
    fen: "rnbqkbnr/pp2pppp/3p4/2p5/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq -",
    moves: [
      { notation: "d4", weight: 100 },   // Main line, open Sicilian
      { notation: "Bb5+", weight: 20 },  // Moscow Variation
      { notation: "c3", weight: 15 },    // Alapin delayed
    ]
  },
  
  // After 1.d4 - Black's main defenses
  {
    name: "Queen's Pawn Opening",
    fen: "rnbqkbnr/pppppppp/8/8/3P4/8/PPP1PPPP/RNBQKBNR b KQkq d3",
    moves: [
      { notation: "Nf6", weight: 100 },  // Indian Defenses (most popular)
      { notation: "d5", weight: 90 },    // Closed Game
      { notation: "e6", weight: 50 },    // French structure
      { notation: "g6", weight: 40 },    // King's Indian/Grunfeld prep
      { notation: "f5", weight: 20 },    // Dutch Defense
    ]
  },
  
  // After 1.d4 Nf6 - White's second move
  {
    name: "Indian Game",
    fen: "rnbqkb1r/pppppppp/5n2/8/3P4/8/PPP1PPPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "c4", weight: 100 },   // Queen's Gambit/Indian Systems
      { notation: "Nf3", weight: 60 },   // Reti System
      { notation: "Bg5", weight: 30 },   // Trompowsky Attack
      { notation: "Nc3", weight: 25 },   // Veresov Opening
    ]
  },
  
  // After 1.d4 d5 - White's second move
  {
    name: "Closed Game (1.d4 d5)",
    fen: "rnbqkbnr/ppp1pppp/8/3p4/3P4/8/PPP1PPPP/RNBQKBNR w KQkq d6",
    moves: [
      { notation: "c4", weight: 100 },   // Queen's Gambit
      { notation: "Nf3", weight: 50 },   // London/Colle System prep
      { notation: "Bf4", weight: 40 },   // London System
      { notation: "Nc3", weight: 30 },   // Veresov/Richter-Veresov
    ]
  },
  
  // After 1.d4 d5 2.c4 - Black's response to Queen's Gambit
  {
    name: "Queen's Gambit",
    fen: "rnbqkbnr/ppp1pppp/8/3p4/2PP4/8/PP2PPPP/RNBQKBNR b KQkq c3",
    moves: [
      { notation: "e6", weight: 100 },   // Queen's Gambit Declined
      { notation: "dxc4", weight: 70 },  // Queen's Gambit Accepted
      { notation: "c6", weight: 60 },    // Slav Defense
      { notation: "Nf6", weight: 40 },   // Various defenses
    ]
  },
  
  // After 1.d4 Nf6 2.c4 - Black's second move
  {
    name: "Indian Defense: 2.c4",
    fen: "rnbqkb1r/pppppppp/5n2/8/2PP4/8/PP2PPPP/RNBQKBNR b KQkq -",
    moves: [
      { notation: "e6", weight: 80 },    // Nimzo/Queen's Indian prep
      { notation: "g6", weight: 70 },    // King's Indian/Grunfeld
      { notation: "e5", weight: 50 },    // Budapest Gambit
      { notation: "c5", weight: 40 },    // Benoni Defense
    ]
  },
  
  // After 1.Nf3 - Black's response to Reti
  {
    name: "Reti Opening",
    fen: "rnbqkbnr/pppppppp/8/8/8/5N2/PPPPPPPP/RNBQKB1R b KQkq -",
    moves: [
      { notation: "d5", weight: 80 },    // Control center
      { notation: "Nf6", weight: 70 },   // Symmetrical
      { notation: "c5", weight: 40 },    // English-like setup
      { notation: "g6", weight: 30 },    // Fianchetto
    ]
  },
  
  // After 1.c4 - Black's response to English
  {
    name: "English Opening",
    fen: "rnbqkbnr/pppppppp/8/8/2P5/8/PP1PPPPP/RNBQKBNR b KQkq c3",
    moves: [
      { notation: "e5", weight: 70 },    // Reversed Sicilian
      { notation: "Nf6", weight: 60 },   // Flexible development
      { notation: "c5", weight: 50 },    // Symmetrical English
      { notation: "e6", weight: 40 },    // Quiet setup
    ]
  },
  
  // After 1.e4 e5 2.Nf3 Nc6 3.Bb5 - Black's response to Ruy Lopez
  {
    name: "Ruy Lopez",
    fen: "r1bqkbnr/pppp1ppp/2n5/1B2p3/4P3/5N2/PPPP1PPP/RNBQK2R b KQkq -",
    moves: [
      { notation: "a6", weight: 100 },   // Morphy Defense (main line)
      { notation: "Nf6", weight: 60 },   // Berlin Defense
      { notation: "Nd4", weight: 30 },   // Bird's Defense
      { notation: "Bc5", weight: 20 },   // Classical Defense
    ]
  },
  
  // After 1.e4 e5 2.Nf3 Nc6 3.Bc4 - Black's response to Italian Game
  {
    name: "Italian Game",
    fen: "r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq -",
    moves: [
      { notation: "Bc5", weight: 100 },  // Giuoco Piano
      { notation: "Nf6", weight: 80 },   // Two Knights Defense
      { notation: "Be7", weight: 40 },   // Hungarian Defense
      { notation: "Nd4", weight: 20 },   // Blackburne Shilling Gambit
    ]
  },
  
  // After 1.e4 e5 2.Nf3 Nc6 3.Bc4 Bc5 - White's move in Giuoco Piano
  {
    name: "Giuoco Piano",
    fen: "r1bqk1nr/pppp1ppp/2n5/2b1p3/2B1P3/5N2/PPPP1PPP/RNBQK2R w KQkq -",
    moves: [
      { notation: "c3", weight: 100 },   // Main line (d4 preparation)
      { notation: "d3", weight: 60 },    // Giuoco Pianissimo
      { notation: "O-O", weight: 40 },   // Quiet development
      { notation: "b4", weight: 30 },    // Evans Gambit
    ]
  },
  
  // After 1.e4 e5 2.Nf3 Nc6 3.d4 - Black's response to Scotch Game
  {
    name: "Scotch Game",
    fen: "r1bqkbnr/pppp1ppp/2n5/4p3/3PP3/5N2/PPP2PPP/RNBQKB1R b KQkq d3",
    moves: [
      { notation: "exd4", weight: 100 }, // Main line
      { notation: "Nf6", weight: 30 },   // Declining the pawn
    ]
  },
  
  // After 1.e4 e5 2.Nf3 Nf6 - White's response to Petrov's Defense
  {
    name: "Petrov's Defense",
    fen: "rnbqkb1r/pppp1ppp/5n2/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq -",
    moves: [
      { notation: "Nxe5", weight: 100 }, // Main line
      { notation: "d4", weight: 40 },    // Steinitz Attack
      { notation: "Nc3", weight: 20 },   // Three Knights Game
    ]
  },
  
  // After 1.e4 e6 - White's response to French Defense
  {
    name: "French Defense",
    fen: "rnbqkbnr/pppp1ppp/4p3/8/4P3/8/PPPP1PPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "d4", weight: 100 },   // Main line
      { notation: "d3", weight: 30 },    // King's Indian Attack
      { notation: "Nf3", weight: 20 },   // Two Knights Variation
    ]
  },
  
  // After 1.e4 e6 2.d4 d5 - White's third move in French Defense
  {
    name: "French Defense: 2.d4 d5",
    fen: "rnbqkbnr/ppp2ppp/4p3/3p4/3PP3/8/PPP2PPP/RNBQKBNR w KQkq d6",
    moves: [
      { notation: "Nc3", weight: 100 },  // Winawer/Classical
      { notation: "Nd2", weight: 80 },   // Tarrasch Variation
      { notation: "exd5", weight: 60 },  // Exchange Variation
      { notation: "e5", weight: 40 },    // Advance Variation
    ]
  },
  
  // After 1.e4 c6 - White's response to Caro-Kann
  {
    name: "Caro-Kann Defense",
    fen: "rnbqkbnr/pp1ppppp/2p5/8/4P3/8/PPPP1PPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "d4", weight: 100 },   // Main line
      { notation: "Nc3", weight: 40 },   // Two Knights Variation
      { notation: "Nf3", weight: 20 },   // Two Knights Attack
    ]
  },
  
  // After 1.e4 c6 2.d4 d5 - White's third move in Caro-Kann
  {
    name: "Caro-Kann Defense: 2.d4 d5",
    fen: "rnbqkbnr/pp2pppp/2p5/3p4/3PP3/8/PPP2PPP/RNBQKBNR w KQkq d6",
    moves: [
      { notation: "Nc3", weight: 100 },  // Classical Variation
      { notation: "Nd2", weight: 70 },   // Classical (alternative move order)
      { notation: "exd5", weight: 60 },  // Exchange Variation
      { notation: "e5", weight: 40 },    // Advance Variation
    ]
  },
  
  // After 1.e4 c5 2.Nf3 Nc6 - White's third move in Old Sicilian
  {
    name: "Sicilian Defense: Old Sicilian",
    fen: "r1bqkbnr/pp1ppppp/2n5/2p5/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq -",
    moves: [
      { notation: "d4", weight: 100 },   // Open Sicilian
      { notation: "Bb5", weight: 50 },   // Rossolimo Variation
      { notation: "Nc3", weight: 30 },   // Closed Sicilian
    ]
  },
  
  // After 1.e4 c5 2.Nf3 e6 - White's third move in Taimanov/Paulsen
  {
    name: "Sicilian Defense: Taimanov/Paulsen",
    fen: "rnbqkbnr/pp1p1ppp/4p3/2p5/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq -",
    moves: [
      { notation: "d4", weight: 100 },   // Main line
      { notation: "Nc3", weight: 40 },   // Closed Sicilian
      { notation: "d3", weight: 20 },    // Delayed d4
    ]
  },
  
  // After 1.d4 Nf6 2.c4 e6 - White's third move
  {
    name: "Indian Defense: 2...e6",
    fen: "rnbqkb1r/pppp1ppp/4pn2/8/2PP4/8/PP2PPPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "Nc3", weight: 100 },  // Nimzo-Indian/Queen's Indian setup
      { notation: "Nf3", weight: 60 },   // Queen's Indian setup
      { notation: "g3", weight: 40 },    // Catalan
    ]
  },
  
  // After 1.d4 Nf6 2.c4 e6 3.Nc3 - Black's response (Nimzo-Indian or Queen's Indian)
  {
    name: "Indian Defense: 3.Nc3",
    fen: "rnbqkb1r/pppp1ppp/4pn2/8/2PP4/2N5/PP2PPPP/R1BQKBNR b KQkq -",
    moves: [
      { notation: "Bb4", weight: 100 },  // Nimzo-Indian Defense
      { notation: "d5", weight: 70 },    // Queen's Gambit Declined structure
      { notation: "b6", weight: 50 },    // Queen's Indian Defense
    ]
  },
  
  // After 1.d4 Nf6 2.c4 e6 3.Nc3 Bb4 - White's response to Nimzo-Indian
  {
    name: "Nimzo-Indian Defense",
    fen: "rnbqk2r/pppp1ppp/4pn2/8/1bPP4/2N5/PP2PPPP/R1BQKBNR w KQkq -",
    moves: [
      { notation: "e3", weight: 100 },   // Rubinstein Variation
      { notation: "Qc2", weight: 80 },   // Classical Variation
      { notation: "Nf3", weight: 60 },   // Three Knights Variation
      { notation: "a3", weight: 40 },    // Saemisch Variation
    ]
  },
  
  // After 1.d4 Nf6 2.c4 g6 - White's third move
  {
    name: "Indian Defense: 2...g6",
    fen: "rnbqkb1r/pppppp1p/5np1/8/2PP4/8/PP2PPPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "Nc3", weight: 100 },  // King's Indian/Grunfeld setup
      { notation: "Nf3", weight: 60 },   // Flexible
      { notation: "g3", weight: 40 },    // Fianchetto System
    ]
  },
  
  // After 1.d4 Nf6 2.c4 g6 3.Nc3 - Black's response (King's Indian or Grunfeld)
  {
    name: "King's Indian/Grunfeld Setup",
    fen: "rnbqkb1r/pppppp1p/5np1/8/2PP4/2N5/PP2PPPP/R1BQKBNR b KQkq -",
    moves: [
      { notation: "Bg7", weight: 80 },   // King's Indian
      { notation: "d5", weight: 70 },    // Grunfeld Defense
    ]
  },
  
  // After 1.d4 Nf6 2.c4 g6 3.Nc3 Bg7 - White's fourth move in King's Indian
  {
    name: "King's Indian Defense",
    fen: "rnbqk2r/ppppppbp/5np1/8/2PP4/2N5/PP2PPPP/R1BQKBNR w KQkq -",
    moves: [
      { notation: "e4", weight: 100 },   // Classical Variation
      { notation: "Nf3", weight: 70 },   // Various systems
      { notation: "g3", weight: 50 },    // Fianchetto Variation
    ]
  },
  
  // After 1.d4 Nf6 2.c4 g6 3.Nc3 d5 - White's response to Grunfeld
  {
    name: "Grunfeld Defense",
    fen: "rnbqkb1r/ppp1pp1p/5np1/3p4/2PP4/2N5/PP2PPPP/R1BQKBNR w KQkq d6",
    moves: [
      { notation: "cxd5", weight: 80 },  // Exchange Variation
      { notation: "Nf3", weight: 70 },   // Main line
      { notation: "Bg5", weight: 30 },   // Russian System
    ]
  },
  
  // After 1.d4 d5 2.c4 e6 - White's third move in QGD
  {
    name: "Queen's Gambit Declined",
    fen: "rnbqkbnr/ppp2ppp/4p3/3p4/2PP4/8/PP2PPPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "Nc3", weight: 100 },  // Main line
      { notation: "Nf3", weight: 80 },   // Alternative move order
      { notation: "cxd5", weight: 40 },  // Exchange Variation early
    ]
  },
  
  // After 1.d4 d5 2.c4 e6 3.Nc3 - Black's response
  {
    name: "Queen's Gambit Declined: 3.Nc3",
    fen: "rnbqkbnr/ppp2ppp/4p3/3p4/2PP4/2N5/PP2PPPP/R1BQKBNR b KQkq -",
    moves: [
      { notation: "Nf6", weight: 100 },  // Main line
      { notation: "c6", weight: 50 },    // Semi-Slav
      { notation: "Be7", weight: 30 },   // Alternative
    ]
  },
  
  // After 1.d4 d5 2.c4 dxc4 - White's response to QGA
  {
    name: "Queen's Gambit Accepted",
    fen: "rnbqkbnr/ppp1pppp/8/8/2pP4/8/PP2PPPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "Nf3", weight: 100 },  // Main line
      { notation: "e3", weight: 60 },    // Simple recapture prep
      { notation: "e4", weight: 40 },    // Central pawn storm
    ]
  },
  
  // After 1.d4 d5 2.c4 c6 - White's third move in Slav Defense
  {
    name: "Slav Defense",
    fen: "rnbqkbnr/pp2pppp/2p5/3p4/2PP4/8/PP2PPPP/RNBQKBNR w KQkq -",
    moves: [
      { notation: "Nf3", weight: 100 },  // Main line
      { notation: "Nc3", weight: 70 },   // Alternative
      { notation: "cxd5", weight: 40 },  // Exchange Slav
    ]
  },
  
  // After 1.Nf3 d5 - White's second move in Reti
  {
    name: "Reti Opening: 1...d5",
    fen: "rnbqkbnr/ppp1pppp/8/3p4/8/5N2/PPPPPPPP/RNBQKB1R w KQkq d6",
    moves: [
      { notation: "d4", weight: 70 },    // Transpose to Queen's Pawn
      { notation: "c4", weight: 60 },    // Reti proper
      { notation: "g3", weight: 40 },    // King's Indian Attack
    ]
  },
  
  // After 1.Nf3 Nf6 - White's second move
  {
    name: "Reti Opening: 1...Nf6",
    fen: "rnbqkb1r/pppppppp/5n2/8/8/5N2/PPPPPPPP/RNBQKB1R w KQkq -",
    moves: [
      { notation: "c4", weight: 80 },    // English-style
      { notation: "d4", weight: 60 },    // Transpose to Queen's Pawn
      { notation: "g3", weight: 50 },    // King's Indian Attack
    ]
  },
];

/**
 * Converts a position's FEN to a simplified key for opening book lookup
 * Removes move counters (halfmove clock and fullmove number) for matching
 * 
 * @param fen - Full FEN string
 * @returns Simplified FEN without move counters
 */
function simplifyFEN(fen: string): string {
  // FEN format: position activeColor castling enPassant halfmove fullmove
  // We only care about: position activeColor castling enPassant
  const parts = fen.split(' ');
  return parts.slice(0, 4).join(' ');
}

/**
 * Looks up a position in the opening book
 * 
 * @param gameState - Current game state
 * @returns Book position if found, null otherwise
 */
function lookupPosition(gameState: GameState): BookPosition | null {
  const fen = toFEN(gameState);
  const simplifiedFEN = simplifyFEN(fen);
  
  // Search opening book for matching position
  const bookPosition = openingBookData.find(pos => {
    const bookFEN = simplifyFEN(pos.fen);
    return bookFEN === simplifiedFEN;
  });
  
  return bookPosition || null;
}

/**
 * Selects a move from book moves using weighted random selection
 * Moves with higher weights are more likely to be selected
 * 
 * @param bookMoves - Array of book moves with weights
 * @returns Selected book move
 */
function selectWeightedMove(bookMoves: BookMove[]): BookMove {
  // Calculate total weight
  const totalWeight = bookMoves.reduce((sum, move) => sum + move.weight, 0);
  
  // Select random value between 0 and totalWeight
  let random = Math.random() * totalWeight;
  
  // Find the move that corresponds to this random value
  for (const move of bookMoves) {
    random -= move.weight;
    if (random <= 0) {
      return move;
    }
  }
  
  // Fallback (should never reach here)
  return bookMoves[bookMoves.length - 1];
}

/**
 * Converts algebraic notation to a Move object
 * 
 * @param gameState - Current game state
 * @param notation - Algebraic notation (e.g., "e4", "Nf3")
 * @returns Corresponding Move object, or null if notation invalid
 */
function parseBookMove(gameState: GameState, notation: string): Move | null {
  const legalMoves = generateLegalMoves(gameState);
  
  // Find move that matches the notation
  for (const move of legalMoves) {
    // We need to make the move to get the resulting state for notation
    const resultingState = makeMove(gameState, move);
    const moveNotation = moveToAlgebraic(move, gameState, resultingState);
    
    // Compare without check/checkmate symbols
    const cleanNotation = notation.replace(/[+#]/g, '');
    const cleanMoveNotation = moveNotation.replace(/[+#]/g, '');
    
    if (cleanMoveNotation === cleanNotation) {
      return move;
    }
  }
  
  return null;
}

/**
 * Gets an opening book move for the current position
 * Returns null if position is not in the opening book
 * 
 * @param gameState - Current game state
 * @returns Opening book move if available, null otherwise
 * 
 * @example
 * // Try to get opening book move
 * const bookMove = getBookMove(gameState);
 * if (bookMove) {
 *   // Play book move (no search needed)
 *   const newState = makeMove(gameState, bookMove);
 * } else {
 *   // Not in book, use search
 *   const bestMove = findBestMove(gameState, depth);
 * }
 */
export function getBookMove(gameState: GameState): Move | null {
  // Look up current position
  const bookPosition = lookupPosition(gameState);
  if (!bookPosition) {
    return null; // Position not in book
  }
  
  // Select a move using weighted random selection
  const selectedBookMove = selectWeightedMove(bookPosition.moves);
  
  // Convert notation to Move object
  const move = parseBookMove(gameState, selectedBookMove.notation);
  
  return move;
}

/**
 * Checks if the current position is in the opening book
 * 
 * @param gameState - Current game state
 * @returns True if position is in book, false otherwise
 */
export function isInBook(gameState: GameState): boolean {
  const bookPosition = lookupPosition(gameState);
  return bookPosition !== null;
}

/**
 * Gets all available book moves for the current position
 * Useful for debugging or showing opening theory to users
 * 
 * @param gameState - Current game state
 * @returns Array of book moves with weights, or empty array if not in book
 */
export function getBookMoves(gameState: GameState): BookMove[] {
  const bookPosition = lookupPosition(gameState);
  return bookPosition ? bookPosition.moves : [];
}

/**
 * Gets the opening name for the current position
 * 
 * @param gameState - Current game state
 * @returns Opening name if position is in book, null otherwise
 * 
 * @example
 * const openingName = getOpeningName(gameState);
 * if (openingName) {
 *   console.log(`Current opening: ${openingName}`);
 * }
 */
export function getOpeningName(gameState: GameState): string | null {
  const bookPosition = lookupPosition(gameState);
  return bookPosition?.name || null;
}

/**
 * Gets complete opening information for the current position
 * Includes opening name and available book moves
 * 
 * @param gameState - Current game state
 * @returns Opening info object or null if not in book
 */
export function getOpeningInfo(gameState: GameState): { name: string; moves: BookMove[] } | null {
  const bookPosition = lookupPosition(gameState);
  if (!bookPosition || !bookPosition.name) {
    return null;
  }
  return {
    name: bookPosition.name,
    moves: bookPosition.moves
  };
}

